// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Button, InlineNotification, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { useNavigate } from 'react-router-dom';
import type { CoverageSession, RedundancyLevel, SiteProfile } from '../../../types';

interface SiteProfileStepProps {
  session: CoverageSession | null;
  saving: boolean;
  error: string | null;
  /** Persist the profile (creating the session first when none exists). */
  onSave: (_profile: SiteProfile, _andContinue: boolean) => void;
}

interface ProfileForm {
  siteName: string;
  address: string;
  latitude: string;
  longitude: string;
  ratedCapacityKw: string;
  ratedCapacityKva: string;
  avgLoadKw: string;
  peakLoadKw: string;
  redundancyLevel: string;
  sourceSequence: string;
  rideThrough: string;
  slaSummary: string;
}

function formFromSession(session: CoverageSession | null): ProfileForm {
  const profile = session?.site_profile;
  const geo = (profile?.geo_coordinates ?? {}) as { latitude?: number; longitude?: number };
  const address = (profile?.address ?? {}) as { text?: string };
  const sla = (profile?.sla ?? {}) as { summary?: string };
  return {
    siteName: profile?.site_name ?? '',
    address: address.text ?? '',
    latitude: geo.latitude != null ? String(geo.latitude) : '',
    longitude: geo.longitude != null ? String(geo.longitude) : '',
    ratedCapacityKw:
      profile?.rated_electrical_capacity_kw != null
        ? String(profile.rated_electrical_capacity_kw)
        : '',
    ratedCapacityKva:
      profile?.rated_electrical_capacity_kva != null
        ? String(profile.rated_electrical_capacity_kva)
        : '',
    avgLoadKw:
      profile?.current_average_load_kw != null ? String(profile.current_average_load_kw) : '',
    peakLoadKw: profile?.current_peak_load_kw != null ? String(profile.current_peak_load_kw) : '',
    redundancyLevel: profile?.redundancy_level ?? '',
    sourceSequence: profile?.source_sequence_description ?? '',
    rideThrough: profile?.ride_through_statement ?? '',
    slaSummary: sla.summary ?? '',
  };
}

/** Validate the form; returns field-keyed messages (empty when valid). */
export function validateProfile(form: ProfileForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.siteName.trim()) errors.siteName = 'Site name is required';
  if (!form.address.trim()) errors.address = 'Address is required';
  if (!form.redundancyLevel) errors.redundancyLevel = 'Redundancy level is required';
  const lat = Number(form.latitude);
  if (form.latitude && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
    errors.latitude = 'Latitude must be between -90 and 90';
  }
  const long = Number(form.longitude);
  if (form.longitude && (Number.isNaN(long) || long < -180 || long > 180)) {
    errors.longitude = 'Longitude must be between -180 and 180';
  }
  for (const key of ['ratedCapacityKw', 'ratedCapacityKva', 'avgLoadKw', 'peakLoadKw'] as const) {
    const value = form[key];
    if (value && (Number.isNaN(Number(value)) || Number(value) < 0)) {
      errors[key] = 'Must be a non-negative number';
    }
  }
  return errors;
}

function profileFromForm(form: ProfileForm): SiteProfile {
  const num = (value: string) => (value.trim() === '' ? null : Number(value));
  return {
    site_name: form.siteName.trim(),
    address: form.address.trim() ? { text: form.address.trim() } : null,
    geo_coordinates:
      form.latitude || form.longitude
        ? { latitude: num(form.latitude), longitude: num(form.longitude) }
        : null,
    rated_electrical_capacity_kw: num(form.ratedCapacityKw),
    rated_electrical_capacity_kva: num(form.ratedCapacityKva),
    current_average_load_kw: num(form.avgLoadKw),
    current_peak_load_kw: num(form.peakLoadKw),
    redundancy_level: (form.redundancyLevel || null) as RedundancyLevel | null,
    source_sequence_description: form.sourceSequence.trim() || null,
    ride_through_statement: form.rideThrough.trim() || null,
    sla: form.slaSummary.trim() ? { summary: form.slaSummary.trim() } : null,
  };
}

/** Step 1 — "Site profile": identify the site. Field set per the design
 * mock; the form seeds from the active session's profile on resume. */
export default function SiteProfileStep({ session, saving, error, onSave }: SiteProfileStepProps) {
  const navigate = useNavigate();
  const [form, setForm] = useState<ProfileForm>(() => formFromSession(session));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const update = <K extends keyof ProfileForm>(key: K, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const save = (andContinue: boolean) => {
    const errors = validateProfile(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onSave(profileFromForm(form), andContinue);
    if (!andContinue) navigate('/home');
  };

  return (
    <div className="obw-step">
      {error && (
        <InlineNotification
          kind="error"
          title="Could not save site profile"
          subtitle={error}
          lowContrast
        />
      )}
      <div className="obw-form-grid">
        <TextInput
          id="obw-site-name"
          labelText="Site name *"
          value={form.siteName}
          invalid={Boolean(fieldErrors.siteName)}
          invalidText={fieldErrors.siteName}
          onChange={e => update('siteName', e.target.value)}
        />
        <TextInput
          id="obw-address"
          labelText="Address *"
          value={form.address}
          invalid={Boolean(fieldErrors.address)}
          invalidText={fieldErrors.address}
          onChange={e => update('address', e.target.value)}
        />
        <TextInput
          id="obw-latitude"
          labelText="Latitude"
          value={form.latitude}
          invalid={Boolean(fieldErrors.latitude)}
          invalidText={fieldErrors.latitude}
          onChange={e => update('latitude', e.target.value)}
        />
        <TextInput
          id="obw-longitude"
          labelText="Longitude"
          value={form.longitude}
          invalid={Boolean(fieldErrors.longitude)}
          invalidText={fieldErrors.longitude}
          onChange={e => update('longitude', e.target.value)}
        />
        <TextInput
          id="obw-rated-kw"
          labelText="Rated electrical capacity (kW)"
          value={form.ratedCapacityKw}
          invalid={Boolean(fieldErrors.ratedCapacityKw)}
          invalidText={fieldErrors.ratedCapacityKw}
          onChange={e => update('ratedCapacityKw', e.target.value)}
        />
        <TextInput
          id="obw-rated-kva"
          labelText="Rated electrical capacity (kVA)"
          value={form.ratedCapacityKva}
          invalid={Boolean(fieldErrors.ratedCapacityKva)}
          invalidText={fieldErrors.ratedCapacityKva}
          onChange={e => update('ratedCapacityKva', e.target.value)}
        />
        <TextInput
          id="obw-avg-load"
          labelText="Current average load (kW)"
          value={form.avgLoadKw}
          invalid={Boolean(fieldErrors.avgLoadKw)}
          invalidText={fieldErrors.avgLoadKw}
          onChange={e => update('avgLoadKw', e.target.value)}
        />
        <TextInput
          id="obw-peak-load"
          labelText="Current peak load (kW)"
          value={form.peakLoadKw}
          invalid={Boolean(fieldErrors.peakLoadKw)}
          invalidText={fieldErrors.peakLoadKw}
          onChange={e => update('peakLoadKw', e.target.value)}
        />
        <Select
          id="obw-redundancy"
          labelText="Redundancy level *"
          value={form.redundancyLevel}
          invalid={Boolean(fieldErrors.redundancyLevel)}
          invalidText={fieldErrors.redundancyLevel}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            update('redundancyLevel', e.target.value)
          }
        >
          <SelectItem value="" text="Select redundancy level..." />
          <SelectItem value="N" text="N (No redundancy)" />
          <SelectItem value="N+1" text="N+1 (Single redundant component)" />
          <SelectItem value="N+2" text="N+2 (Dual redundant components)" />
          <SelectItem value="2N" text="2N (Fully redundant system)" />
          <SelectItem value="2N+1" text="2N+1 (Fully redundant + one extra)" />
        </Select>
      </div>
      <TextArea
        id="obw-source-sequence"
        labelText="Source sequence description"
        placeholder="grid to ATS/STS to generators to UPS to PDUs/RPPs to branch circuits"
        value={form.sourceSequence}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          update('sourceSequence', e.target.value)
        }
        rows={2}
      />
      <TextArea
        id="obw-ride-through"
        labelText="Ride-through statement"
        helperText="Optional. Attachable as part of the site profile document in step 2."
        value={form.rideThrough}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          update('rideThrough', e.target.value)
        }
        rows={2}
      />
      <TextArea
        id="obw-sla"
        labelText="SLA summary"
        value={form.slaSummary}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          update('slaSummary', e.target.value)
        }
        rows={2}
      />
      <div className="obw-step-actions">
        <Button kind="secondary" disabled={saving} onClick={() => save(false)}>
          Save and exit
        </Button>
        <Button disabled={saving} onClick={() => save(true)}>
          Save and continue
        </Button>
      </div>
    </div>
  );
}
